import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DirectusService } from '../core/services/directus.service';

// ── Shared styles ──────────────────────────────────────────────────────────────

const SHARED_STYLES = `
  :host { display: block; }
  .wc-page { display: flex; flex-direction: column; gap: 20px; }

  /* Header */
  .wc-header {
    display: flex; align-items: flex-start;
    justify-content: space-between; gap: 16px;
  }
  .wc-header-text h1 {
    margin: 0 0 4px; font-size: 1.75rem;
    font-weight: 700; color: var(--app-ink-strong);
  }
  .wc-header-text p { margin: 0; color: var(--app-muted); font-size: 0.9rem; }
  .wc-breadcrumb {
    margin: 0 0 4px;
    font-size: 0.78rem; color: var(--app-muted); font-weight: 500;
  }
  .wc-breadcrumb span { color: var(--app-text); }

  /* Section cards */
  .section-card {
    border: 1px solid var(--app-border);
    border-radius: var(--app-radius);
    background: var(--app-surface);
    overflow: hidden;
  }
  .section-card-head {
    display: flex; align-items: center; gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--app-border);
    background: var(--app-surface-soft);
  }
  .section-card-head h2 {
    margin: 0; font-size: 0.95rem; font-weight: 700;
    color: var(--app-ink-strong);
  }
  .hint-inline { font-size: 0.78rem; color: var(--app-muted); }

  /* Form grid */
  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    padding: 20px;
  }
  .field { display: flex; flex-direction: column; gap: 6px; }
  .field.full { grid-column: 1 / -1; }
  .field-label { font-size: 0.82rem; font-weight: 600; color: var(--app-text); }
  .req { color: var(--app-danger); }
  .field-input {
    width: 100%; box-sizing: border-box;
    padding: 9px 12px;
    border: 1px solid var(--app-border);
    border-radius: 8px;
    background: var(--app-input-bg);
    color: var(--app-text);
    font-size: 0.9rem; font-family: inherit;
    outline: none;
    transition: border-color 140ms;
  }
  .field-input:focus { border-color: var(--jci-blue); background: var(--app-surface); }
  textarea.field-input { resize: vertical; min-height: 80px; }

  /* Save bar */
  .save-bar { display: flex; justify-content: flex-end; padding: 4px 0 8px; }

  /* Feedback */
  .feedback {
    padding: 12px 16px;
    border-radius: var(--app-radius);
    font-size: 0.875rem; font-weight: 500;
  }
  .feedback.success { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
  .feedback.error   { background: #fff1f2; color: #991b1b; border: 1px solid #fecaca; }

  /* Loading */
  .loading-state {
    display: flex; align-items: center; gap: 10px;
    color: var(--app-muted); font-size: 0.9rem; padding: 32px 0;
  }
  .spinner {
    width: 18px; height: 18px;
    border: 2px solid var(--app-border);
    border-top-color: var(--jci-blue);
    border-radius: 50%; display: inline-block;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Toolbar */
  .list-toolbar {
    display: flex; align-items: center;
    justify-content: space-between; gap: 12px;
  }
  .list-count { font-size: 0.85rem; font-weight: 600; color: var(--app-muted); }
  .list-empty {
    padding: 40px; text-align: center;
    color: var(--app-muted); font-size: 0.9rem;
    border: 1px dashed var(--app-border);
    border-radius: var(--app-radius);
    background: var(--app-surface-soft);
  }

  /* Announcements table */
  .announcements-table {
    border: 1px solid var(--app-border);
    border-radius: var(--app-radius);
    background: var(--app-surface);
    overflow: hidden;
  }
  .ann-row {
    display: flex; align-items: center; gap: 16px;
    padding: 14px 18px;
    border-bottom: 1px solid var(--app-border);
  }
  .ann-row:last-child { border-bottom: none; }
  .ann-status { display: flex; align-items: center; gap: 7px; flex-shrink: 0; }
  .status-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--app-border); flex-shrink: 0;
  }
  .status-dot.published { background: #22c55e; }
  .status-label { font-size: 0.78rem; font-weight: 600; color: var(--app-muted); white-space: nowrap; }
  .ann-body { flex: 1; min-width: 0; }
  .ann-title { font-size: 0.9rem; font-weight: 600; color: var(--app-ink-strong); }
  .ann-dates { font-size: 0.78rem; color: var(--app-muted); margin-top: 2px; }
  .ann-actions { display: flex; gap: 6px; flex-shrink: 0; }

  /* Toggle */
  .toggle-label {
    display: flex; align-items: center; gap: 10px;
    font-size: 0.9rem; font-weight: 500; color: var(--app-text);
    cursor: pointer;
  }
  .toggle-label input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; }

  /* Buttons */
  .btn-primary {
    padding: 9px 20px; border-radius: 999px;
    border: none; background: var(--jci-blue); color: #fff;
    font-size: 0.875rem; font-weight: 600;
    cursor: pointer; font-family: inherit; transition: opacity 140ms;
  }
  .btn-primary:hover:not(:disabled) { opacity: 0.88; }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

  .btn-ghost {
    padding: 7px 14px; border-radius: 999px;
    border: 1px solid transparent; background: transparent;
    color: var(--app-muted); font-size: 0.875rem; font-weight: 500;
    cursor: pointer; font-family: inherit; transition: background 140ms, color 140ms;
  }
  .btn-ghost:hover { background: var(--app-surface-soft); color: var(--app-text); }
  .btn-ghost.xs { padding: 4px 10px; font-size: 0.78rem; }
  .btn-ghost.danger { color: var(--app-danger); }
  .btn-ghost.danger:hover { background: #fff1f2; }

  .btn-ghost-sm {
    padding: 6px 14px; border-radius: 999px;
    border: 1px solid var(--app-border); background: transparent;
    color: var(--app-muted); font-size: 0.82rem; font-weight: 500;
    cursor: pointer; text-decoration: none; display: inline-block;
    transition: border-color 140ms, color 140ms; flex-shrink: 0; margin-top: 4px;
  }
  .btn-ghost-sm:hover { border-color: var(--jci-blue); color: var(--jci-blue); }

  .btn-danger {
    padding: 9px 20px; border-radius: 999px;
    border: none; background: var(--app-danger); color: #fff;
    font-size: 0.875rem; font-weight: 600;
    cursor: pointer; font-family: inherit; transition: opacity 140ms;
  }
  .btn-danger:hover:not(:disabled) { opacity: 0.88; }
  .btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Overlay / panel */
  .overlay {
    position: fixed; inset: 0;
    background: rgba(19,15,45,0.45);
    display: flex; align-items: center; justify-content: center;
    z-index: 100; padding: 24px;
    backdrop-filter: blur(2px);
  }
  .panel {
    background: var(--app-surface);
    border-radius: 14px;
    box-shadow: 0 24px 48px rgba(19,15,45,0.18);
    display: flex; flex-direction: column;
    width: 100%; max-width: 480px; max-height: 90vh; overflow: hidden;
  }
  .panel--sm { max-width: 360px; }
  .panel-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 20px 24px 16px;
    border-bottom: 1px solid var(--app-border);
  }
  .panel-head h3 { margin: 0; font-size: 1rem; font-weight: 700; color: var(--app-ink-strong); }
  .panel-close {
    width: 28px; height: 28px; border-radius: 50%;
    border: 1px solid var(--app-border); background: transparent;
    color: var(--app-muted); cursor: pointer; font-size: 13px;
    display: flex; align-items: center; justify-content: center;
    line-height: 1; font-family: inherit;
  }
  .panel-close:hover { background: var(--app-surface-soft); color: var(--app-text); }
  .panel-body { padding: 24px; overflow-y: auto; flex: 1; }
  .panel-body p { margin: 0; color: var(--app-text); font-size: 0.9rem; line-height: 1.5; }
  .panel-foot {
    display: flex; justify-content: flex-end; gap: 10px;
    padding: 16px 24px;
    border-top: 1px solid var(--app-border);
  }

  @media (max-width: 720px) {
    .form-grid { grid-template-columns: 1fr; }
    .field.full { grid-column: 1; }
  }
`;

// ── Interfaces ────────────────────────────────────────────────────────────────

interface HomeData {
  id: number;
  hero_title: string;
  hero_subtitle: string;
  cta_primary_text: string;
  cta_primary_link: string;
  cta_secondary_text: string;
  cta_secondary_link: string;
  stat_members: string;
  stat_projects: string;
  stat_years: string;
  stat_lives_impacted: string;
  president_message_override: string;
}

interface AboutData {
  id: number;
  hero_title: string;
  hero_subtitle: string;
  mission_text: string;
  vision_text: string;
}

interface SettingsData {
  id: number;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  instagram_url: string;
  facebook_url: string;
  linkedin_url: string;
  twitter_url: string;
  footer_tagline: string;
}

interface Announcement {
  id: number;
  title: string;
  description: string;
  startdate: string;
  enddate: string;
  published: boolean;
}

interface AnnouncementForm {
  title: string;
  description: string;
  startdate: string;
  enddate: string;
  published: boolean;
}

// ── HOME PAGE ─────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-wc-home',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="wc-page">
      <header class="wc-header">
        <div class="wc-header-text">
          <p class="wc-breadcrumb">Website Content › <span>Home Page</span></p>
          <h1>Home Page</h1>
          <p>Edit the public home page hero, CTAs, and stats.</p>
        </div>
        <a class="btn-ghost-sm" href="https://staging.data.jcinusantara.or.id/admin" target="_blank" rel="noopener">
          Open Directus ↗
        </a>
      </header>

      <div class="feedback success" *ngIf="successMsg">{{ successMsg }}</div>
      <div class="feedback error"   *ngIf="errorMsg">{{ errorMsg }}</div>

      <div class="loading-state" *ngIf="loading">
        <span class="spinner"></span> Loading…
      </div>

      <ng-container *ngIf="!loading && home">
        <div class="section-card">
          <div class="section-card-head"><h2>Hero Section</h2></div>
          <div class="form-grid">
            <div class="field full">
              <label class="field-label">Hero title</label>
              <input class="field-input" type="text" [(ngModel)]="home.hero_title" />
            </div>
            <div class="field full">
              <label class="field-label">Hero subtitle</label>
              <textarea class="field-input" rows="3" [(ngModel)]="home.hero_subtitle"></textarea>
            </div>
          </div>
        </div>

        <div class="section-card">
          <div class="section-card-head"><h2>Call to Action Buttons</h2></div>
          <div class="form-grid">
            <div class="field">
              <label class="field-label">Primary button text</label>
              <input class="field-input" type="text" [(ngModel)]="home.cta_primary_text" placeholder="e.g. Become a Member" />
            </div>
            <div class="field">
              <label class="field-label">Primary button link</label>
              <input class="field-input" type="text" [(ngModel)]="home.cta_primary_link" placeholder="/membership" />
            </div>
            <div class="field">
              <label class="field-label">Secondary button text</label>
              <input class="field-input" type="text" [(ngModel)]="home.cta_secondary_text" placeholder="e.g. Learn More" />
            </div>
            <div class="field">
              <label class="field-label">Secondary button link</label>
              <input class="field-input" type="text" [(ngModel)]="home.cta_secondary_link" placeholder="/about" />
            </div>
          </div>
        </div>

        <div class="section-card">
          <div class="section-card-head">
            <h2>Impact Stats</h2>
            <span class="hint-inline">Shown in the hero section as social proof</span>
          </div>
          <div class="form-grid">
            <div class="field">
              <label class="field-label">Members</label>
              <input class="field-input" type="text" [(ngModel)]="home.stat_members" placeholder="e.g. 40+" />
            </div>
            <div class="field">
              <label class="field-label">Projects</label>
              <input class="field-input" type="text" [(ngModel)]="home.stat_projects" placeholder="e.g. 50+" />
            </div>
            <div class="field">
              <label class="field-label">Years</label>
              <input class="field-input" type="text" [(ngModel)]="home.stat_years" placeholder="e.g. 3" />
            </div>
            <div class="field">
              <label class="field-label">Lives impacted</label>
              <input class="field-input" type="text" [(ngModel)]="home.stat_lives_impacted" placeholder="e.g. 1,000+" />
            </div>
          </div>
        </div>

        <div class="section-card">
          <div class="section-card-head">
            <h2>President Message Override</h2>
            <span class="hint-inline">Leave blank to use the president's own message from the Presidents page</span>
          </div>
          <div class="form-grid">
            <div class="field full">
              <textarea class="field-input" rows="6" [(ngModel)]="home.president_message_override"
                        placeholder="Override the homepage president quote here…"></textarea>
            </div>
          </div>
        </div>

        <div class="save-bar">
          <button class="btn-primary" (click)="save()" [disabled]="saving">
            {{ saving ? 'Saving…' : 'Save Home Page' }}
          </button>
        </div>
      </ng-container>
    </section>
  `,
  styles: [SHARED_STYLES]
})
export class WcHomeComponent implements OnInit {
  loading = false;
  saving  = false;
  successMsg = '';
  errorMsg   = '';
  home: HomeData | null = null;

  constructor(private readonly directus: DirectusService) {}

  ngOnInit(): void { void this.load(); }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const r = await this.directus.getSingleton('page_home');
      this.home = {
        id: r['id'] as number,
        hero_title: String(r['hero_title'] ?? ''),
        hero_subtitle: String(r['hero_subtitle'] ?? ''),
        cta_primary_text: String(r['cta_primary_text'] ?? ''),
        cta_primary_link: String(r['cta_primary_link'] ?? ''),
        cta_secondary_text: String(r['cta_secondary_text'] ?? ''),
        cta_secondary_link: String(r['cta_secondary_link'] ?? ''),
        stat_members: String(r['stat_members'] ?? ''),
        stat_projects: String(r['stat_projects'] ?? ''),
        stat_years: String(r['stat_years'] ?? ''),
        stat_lives_impacted: String(r['stat_lives_impacted'] ?? ''),
        president_message_override: String(r['president_message_override'] ?? '')
      };
    } catch { this.errorMsg = 'Failed to load.'; }
    finally { this.loading = false; }
  }

  async save(): Promise<void> {
    if (!this.home || this.saving) return;
    this.saving = true; this.clearMessages();
    try {
      await this.directus.update('page_home', this.home.id, {
        hero_title: this.home.hero_title,
        hero_subtitle: this.home.hero_subtitle,
        cta_primary_text: this.home.cta_primary_text,
        cta_primary_link: this.home.cta_primary_link,
        cta_secondary_text: this.home.cta_secondary_text,
        cta_secondary_link: this.home.cta_secondary_link,
        stat_members: this.home.stat_members,
        stat_projects: this.home.stat_projects,
        stat_years: this.home.stat_years,
        stat_lives_impacted: this.home.stat_lives_impacted,
        president_message_override: this.home.president_message_override || null
      });
      this.showSuccess('Home page saved.');
    } catch { this.errorMsg = 'Failed to save.'; }
    finally { this.saving = false; }
  }

  private showSuccess(msg: string): void {
    this.successMsg = msg;
    setTimeout(() => (this.successMsg = ''), 3500);
  }
  private clearMessages(): void { this.successMsg = ''; this.errorMsg = ''; }
}

// ── ABOUT PAGE ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-wc-about',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="wc-page">
      <header class="wc-header">
        <div class="wc-header-text">
          <p class="wc-breadcrumb">Website Content › <span>About Page</span></p>
          <h1>About Page</h1>
          <p>Edit the public about page hero, mission, and vision.</p>
        </div>
        <a class="btn-ghost-sm" href="https://staging.data.jcinusantara.or.id/admin" target="_blank" rel="noopener">
          Open Directus ↗
        </a>
      </header>

      <div class="feedback success" *ngIf="successMsg">{{ successMsg }}</div>
      <div class="feedback error"   *ngIf="errorMsg">{{ errorMsg }}</div>

      <div class="loading-state" *ngIf="loading">
        <span class="spinner"></span> Loading…
      </div>

      <ng-container *ngIf="!loading && about">
        <div class="section-card">
          <div class="section-card-head"><h2>Hero Section</h2></div>
          <div class="form-grid">
            <div class="field full">
              <label class="field-label">Hero title</label>
              <input class="field-input" type="text" [(ngModel)]="about.hero_title" />
            </div>
            <div class="field full">
              <label class="field-label">Hero subtitle / intro paragraph</label>
              <textarea class="field-input" rows="4" [(ngModel)]="about.hero_subtitle"></textarea>
            </div>
          </div>
        </div>

        <div class="section-card">
          <div class="section-card-head"><h2>Mission &amp; Vision</h2></div>
          <div class="form-grid">
            <div class="field full">
              <label class="field-label">Mission statement</label>
              <textarea class="field-input" rows="3" [(ngModel)]="about.mission_text"></textarea>
            </div>
            <div class="field full">
              <label class="field-label">Vision statement</label>
              <textarea class="field-input" rows="3" [(ngModel)]="about.vision_text"></textarea>
            </div>
          </div>
        </div>

        <div class="save-bar">
          <button class="btn-primary" (click)="save()" [disabled]="saving">
            {{ saving ? 'Saving…' : 'Save About Page' }}
          </button>
        </div>
      </ng-container>
    </section>
  `,
  styles: [SHARED_STYLES]
})
export class WcAboutComponent implements OnInit {
  loading = false;
  saving  = false;
  successMsg = '';
  errorMsg   = '';
  about: AboutData | null = null;

  constructor(private readonly directus: DirectusService) {}

  ngOnInit(): void { void this.load(); }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const r = await this.directus.getSingleton('page_about');
      this.about = {
        id: r['id'] as number,
        hero_title: String(r['hero_title'] ?? ''),
        hero_subtitle: String(r['hero_subtitle'] ?? ''),
        mission_text: String(r['mission_text'] ?? ''),
        vision_text: String(r['vision_text'] ?? '')
      };
    } catch { this.errorMsg = 'Failed to load.'; }
    finally { this.loading = false; }
  }

  async save(): Promise<void> {
    if (!this.about || this.saving) return;
    this.saving = true; this.clearMessages();
    try {
      await this.directus.update('page_about', this.about.id, {
        hero_title: this.about.hero_title,
        hero_subtitle: this.about.hero_subtitle,
        mission_text: this.about.mission_text,
        vision_text: this.about.vision_text
      });
      this.showSuccess('About page saved.');
    } catch { this.errorMsg = 'Failed to save.'; }
    finally { this.saving = false; }
  }

  private showSuccess(msg: string): void {
    this.successMsg = msg;
    setTimeout(() => (this.successMsg = ''), 3500);
  }
  private clearMessages(): void { this.successMsg = ''; this.errorMsg = ''; }
}

// ── SITE SETTINGS ─────────────────────────────────────────────────────────────

@Component({
  selector: 'app-wc-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="wc-page">
      <header class="wc-header">
        <div class="wc-header-text">
          <p class="wc-breadcrumb">Website Content › <span>Site Settings</span></p>
          <h1>Site Settings</h1>
          <p>Contact information, social links, and footer copy.</p>
        </div>
        <a class="btn-ghost-sm" href="https://staging.data.jcinusantara.or.id/admin" target="_blank" rel="noopener">
          Open Directus ↗
        </a>
      </header>

      <div class="feedback success" *ngIf="successMsg">{{ successMsg }}</div>
      <div class="feedback error"   *ngIf="errorMsg">{{ errorMsg }}</div>

      <div class="loading-state" *ngIf="loading">
        <span class="spinner"></span> Loading…
      </div>

      <ng-container *ngIf="!loading && settings">
        <div class="section-card">
          <div class="section-card-head"><h2>Contact Information</h2></div>
          <div class="form-grid">
            <div class="field">
              <label class="field-label">Email</label>
              <input class="field-input" type="email" [(ngModel)]="settings.contact_email" placeholder="info@example.com" />
            </div>
            <div class="field">
              <label class="field-label">Phone</label>
              <input class="field-input" type="text" [(ngModel)]="settings.contact_phone" placeholder="+62 ..." />
            </div>
            <div class="field full">
              <label class="field-label">Address</label>
              <input class="field-input" type="text" [(ngModel)]="settings.contact_address" placeholder="Jakarta, Indonesia" />
            </div>
          </div>
        </div>

        <div class="section-card">
          <div class="section-card-head"><h2>Social Media Links</h2></div>
          <div class="form-grid">
            <div class="field">
              <label class="field-label">Instagram</label>
              <input class="field-input" type="url" [(ngModel)]="settings.instagram_url" placeholder="https://instagram.com/..." />
            </div>
            <div class="field">
              <label class="field-label">Facebook</label>
              <input class="field-input" type="url" [(ngModel)]="settings.facebook_url" placeholder="https://facebook.com/..." />
            </div>
            <div class="field">
              <label class="field-label">LinkedIn</label>
              <input class="field-input" type="url" [(ngModel)]="settings.linkedin_url" placeholder="https://linkedin.com/company/..." />
            </div>
            <div class="field">
              <label class="field-label">Twitter / X</label>
              <input class="field-input" type="url" [(ngModel)]="settings.twitter_url" placeholder="https://twitter.com/..." />
            </div>
          </div>
        </div>

        <div class="section-card">
          <div class="section-card-head"><h2>Footer</h2></div>
          <div class="form-grid">
            <div class="field full">
              <label class="field-label">Footer tagline</label>
              <textarea class="field-input" rows="2" [(ngModel)]="settings.footer_tagline"
                        placeholder="Empowering young leaders…"></textarea>
            </div>
          </div>
        </div>

        <div class="save-bar">
          <button class="btn-primary" (click)="save()" [disabled]="saving">
            {{ saving ? 'Saving…' : 'Save Site Settings' }}
          </button>
        </div>
      </ng-container>
    </section>
  `,
  styles: [SHARED_STYLES]
})
export class WcSettingsComponent implements OnInit {
  loading = false;
  saving  = false;
  successMsg = '';
  errorMsg   = '';
  settings: SettingsData | null = null;

  constructor(private readonly directus: DirectusService) {}

  ngOnInit(): void { void this.load(); }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const r = await this.directus.getSingleton('site_settings');
      this.settings = {
        id: r['id'] as number,
        contact_email: String(r['contact_email'] ?? ''),
        contact_phone: String(r['contact_phone'] ?? ''),
        contact_address: String(r['contact_address'] ?? ''),
        instagram_url: String(r['instagram_url'] ?? ''),
        facebook_url: String(r['facebook_url'] ?? ''),
        linkedin_url: String(r['linkedin_url'] ?? ''),
        twitter_url: String(r['twitter_url'] ?? ''),
        footer_tagline: String(r['footer_tagline'] ?? '')
      };
    } catch { this.errorMsg = 'Failed to load.'; }
    finally { this.loading = false; }
  }

  async save(): Promise<void> {
    if (!this.settings || this.saving) return;
    this.saving = true; this.clearMessages();
    try {
      await this.directus.update('site_settings', this.settings.id, {
        contact_email: this.settings.contact_email,
        contact_phone: this.settings.contact_phone,
        contact_address: this.settings.contact_address,
        instagram_url: this.settings.instagram_url,
        facebook_url: this.settings.facebook_url,
        linkedin_url: this.settings.linkedin_url,
        twitter_url: this.settings.twitter_url,
        footer_tagline: this.settings.footer_tagline
      });
      this.showSuccess('Site settings saved.');
    } catch { this.errorMsg = 'Failed to save.'; }
    finally { this.saving = false; }
  }

  private showSuccess(msg: string): void {
    this.successMsg = msg;
    setTimeout(() => (this.successMsg = ''), 3500);
  }
  private clearMessages(): void { this.successMsg = ''; this.errorMsg = ''; }
}

// ── ANNOUNCEMENTS ─────────────────────────────────────────────────────────────

@Component({
  selector: 'app-wc-announcements',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="wc-page">
      <header class="wc-header">
        <div class="wc-header-text">
          <p class="wc-breadcrumb">Website Content › <span>Announcements</span></p>
          <h1>Announcements</h1>
          <p>Manage banners and notices shown on the public website.</p>
        </div>
        <button class="btn-primary" (click)="openAdd()">+ New Announcement</button>
      </header>

      <div class="feedback success" *ngIf="successMsg">{{ successMsg }}</div>
      <div class="feedback error"   *ngIf="errorMsg">{{ errorMsg }}</div>

      <div class="loading-state" *ngIf="loading">
        <span class="spinner"></span> Loading…
      </div>

      <ng-container *ngIf="!loading">
        <div class="list-toolbar">
          <span class="list-count">{{ announcements.length }} announcement{{ announcements.length !== 1 ? 's' : '' }}</span>
        </div>

        <div class="announcements-table" *ngIf="announcements.length > 0">
          <div class="ann-row" *ngFor="let a of announcements">
            <div class="ann-status">
              <span class="status-dot" [class.published]="a.published"></span>
              <span class="status-label">{{ a.published ? 'Published' : 'Draft' }}</span>
            </div>
            <div class="ann-body">
              <div class="ann-title">{{ a.title }}</div>
              <div class="ann-dates" *ngIf="a.startdate || a.enddate">
                {{ a.startdate | date:'d MMM y' }}
                <ng-container *ngIf="a.enddate"> — {{ a.enddate | date:'d MMM y' }}</ng-container>
              </div>
            </div>
            <div class="ann-actions">
              <button class="btn-ghost xs" (click)="openEdit(a)">Edit</button>
              <button class="btn-ghost xs danger" (click)="confirmDelete(a)">Delete</button>
            </div>
          </div>
        </div>

        <div class="list-empty" *ngIf="announcements.length === 0">
          No announcements yet.
        </div>
      </ng-container>
    </section>

    <!-- Form overlay -->
    <div class="overlay" *ngIf="formOpen" (click)="closeForm()">
      <div class="panel" (click)="$event.stopPropagation()">
        <div class="panel-head">
          <h3>{{ editing ? 'Edit Announcement' : 'New Announcement' }}</h3>
          <button class="panel-close" (click)="closeForm()">✕</button>
        </div>
        <div class="panel-body" *ngIf="form">
          <div class="form-grid">
            <div class="field full">
              <label class="field-label">Title <span class="req">*</span></label>
              <input class="field-input" type="text" [(ngModel)]="form.title" />
            </div>
            <div class="field full">
              <label class="field-label">Description</label>
              <textarea class="field-input" rows="4" [(ngModel)]="form.description"
                        placeholder="Announcement details…"></textarea>
            </div>
            <div class="field">
              <label class="field-label">Start date</label>
              <input class="field-input" type="date" [(ngModel)]="form.startdate" />
            </div>
            <div class="field">
              <label class="field-label">End date</label>
              <input class="field-input" type="date" [(ngModel)]="form.enddate" />
            </div>
            <div class="field full">
              <label class="toggle-label">
                <input type="checkbox" [(ngModel)]="form.published" />
                <span>Published (visible on website)</span>
              </label>
            </div>
          </div>
        </div>
        <div class="panel-foot">
          <button class="btn-ghost" (click)="closeForm()">Cancel</button>
          <button class="btn-primary" (click)="save()" [disabled]="saving || !form?.title">
            {{ saving ? 'Saving…' : (editing ? 'Save changes' : 'Create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete confirm -->
    <div class="overlay" *ngIf="deleteTarget" (click)="cancelDelete()">
      <div class="panel panel--sm" (click)="$event.stopPropagation()">
        <div class="panel-head">
          <h3>Delete announcement?</h3>
        </div>
        <div class="panel-body">
          <p>Remove <strong>{{ deleteTarget.title }}</strong>? This cannot be undone.</p>
        </div>
        <div class="panel-foot">
          <button class="btn-ghost" (click)="cancelDelete()">Cancel</button>
          <button class="btn-danger" (click)="executeDelete()" [disabled]="saving">
            {{ saving ? 'Deleting…' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [SHARED_STYLES]
})
export class WcAnnouncementsComponent implements OnInit {
  loading = false;
  saving  = false;
  successMsg = '';
  errorMsg   = '';

  announcements: Announcement[] = [];
  formOpen = false;
  editing: Announcement | null = null;
  form: AnnouncementForm | null = null;
  deleteTarget: Announcement | null = null;

  constructor(private readonly directus: DirectusService) {}

  ngOnInit(): void { void this.load(); }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const rows = await this.directus.list('announcements', { sort: '-id', limit: 100 });
      this.announcements = rows.map(r => ({
        id: r['id'] as number,
        title: String(r['title'] ?? ''),
        description: String(r['description'] ?? ''),
        startdate: String(r['startdate'] ?? ''),
        enddate: String(r['enddate'] ?? ''),
        published: Boolean(r['published'])
      }));
    } catch { this.errorMsg = 'Failed to load.'; }
    finally { this.loading = false; }
  }

  openAdd(): void {
    this.editing = null;
    this.form = { title: '', description: '', startdate: '', enddate: '', published: false };
    this.formOpen = true;
    this.clearMessages();
  }

  openEdit(a: Announcement): void {
    this.editing = a;
    this.form = { ...a };
    this.formOpen = true;
    this.clearMessages();
  }

  closeForm(): void {
    this.formOpen = false;
    this.editing = null;
    this.form = null;
  }

  async save(): Promise<void> {
    if (!this.form || this.saving) return;
    this.saving = true; this.clearMessages();
    try {
      const payload: Record<string, unknown> = {
        title: this.form.title,
        description: this.form.description || null,
        startdate: this.form.startdate || null,
        enddate: this.form.enddate || null,
        published: this.form.published
      };
      if (this.editing) {
        await this.directus.update('announcements', this.editing.id, payload);
        this.showSuccess('Announcement updated.');
      } else {
        await this.directus.create('announcements', payload);
        this.showSuccess('Announcement created.');
      }
      this.formOpen = false;
      await this.load();
    } catch { this.errorMsg = 'Failed to save.'; }
    finally { this.saving = false; }
  }

  confirmDelete(a: Announcement): void { this.deleteTarget = a; }
  cancelDelete(): void { this.deleteTarget = null; }

  async executeDelete(): Promise<void> {
    if (!this.deleteTarget || this.saving) return;
    this.saving = true; this.clearMessages();
    try {
      await this.directus.remove('announcements', this.deleteTarget.id);
      this.announcements = this.announcements.filter(a => a.id !== this.deleteTarget!.id);
      this.showSuccess('Announcement deleted.');
      this.deleteTarget = null;
    } catch { this.errorMsg = 'Failed to delete.'; this.deleteTarget = null; }
    finally { this.saving = false; }
  }

  private showSuccess(msg: string): void {
    this.successMsg = msg;
    setTimeout(() => (this.successMsg = ''), 3500);
  }
  private clearMessages(): void { this.successMsg = ''; this.errorMsg = ''; }
}
